import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import {
  Users,
  Clock,
  TrendingUp,
  Download,
  FileSpreadsheet,
  Activity,
  CheckCircle2,
} from 'lucide-react';
import { notify } from '@/lib/notify';
import { PeriodSelector, type PeriodValue } from '@/pages/dashboard/components';
import {
  useStaffMetrics,
  useProcessingTimes,
  useExportStaffMetrics,
  useExportSalesReport,
} from '@/hooks/useStaffAnalytics';

export function StaffAnalyticsDashboard() {
  const [period, setPeriod] = useState<PeriodValue>({ type: 'month' });

  const { data: metricsData, isLoading: isMetricsLoading } = useStaffMetrics(
    period.type,
    period.startDate,
    period.endDate
  );

  const { data: processingTimesData, isLoading: isProcessingLoading } = useProcessingTimes(
    period.type,
    period.startDate,
    period.endDate
  );

  const exportMetricsMutation = useExportStaffMetrics();
  const exportSalesMutation = useExportSalesReport();

  const handleExportMetrics = async () => {
    try {
      await exportMetricsMutation.mutateAsync({
        period: period.type,
        startDate: period.startDate,
        endDate: period.endDate,
      });
      notify.success('Отчет метрик скачан');
    } catch (_error) {
      notify.error('Ошибка скачивания отчета');
    }
  };

  const handleExportSales = async () => {
    try {
      await exportSalesMutation.mutateAsync({
        period: period.type,
        startDate: period.startDate,
        endDate: period.endDate,
      });
      notify.success('Отчет по продажам скачан');
    } catch (_error) {
      notify.error('Ошибка скачивания отчета');
    }
  };

  const metrics = metricsData?.metrics ?? [];
  const totals = metricsData?.totals;
  const processingTimes = processingTimesData?.data ?? [];

  // Подготовка данных для графика конверсии
  const conversionChartData = metrics.map((m) => ({
    name: m.username,
    orders: m.ordersProcessed,
    conversions: m.conversions,
    rate: m.conversionRate,
  }));

  // Подготовка данных для графика времени обработки
  const processingChartData = processingTimes.map((t) => ({
    name: t.username,
    avg: t.avgMinutes,
    median: t.medianMinutes,
    min: t.minMinutes,
    max: t.maxMinutes,
  }));

  if (isMetricsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Аналитика сотрудников</h1>
            <p className="text-muted-foreground">Метрики эффективности команды</p>
          </div>
          <Skeleton className="h-10 w-[180px]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[100px]" />
          ))}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Аналитика сотрудников</h1>
          <p className="text-muted-foreground">Метрики эффективности команды</p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodSelector value={period} onChange={setPeriod} />
          <Button
            variant="outline"
            onClick={handleExportMetrics}
            disabled={exportMetricsMutation.isPending}
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Метрики
          </Button>
          <Button
            variant="outline"
            onClick={handleExportSales}
            disabled={exportSalesMutation.isPending}
          >
            <Download className="h-4 w-4 mr-2" />
            Продажи
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Обработано заказов</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(totals?.totalOrders ?? 0).toLocaleString('ru-RU')}
            </div>
            <p className="text-xs text-muted-foreground">За период</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Смен статуса</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(totals?.totalStatusChanges ?? 0).toLocaleString('ru-RU')}
            </div>
            <p className="text-xs text-muted-foreground">Действий</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Конверсий</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {(totals?.totalConversions ?? 0).toLocaleString('ru-RU')}
            </div>
            <p className="text-xs text-muted-foreground">Успешных сделок</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Средняя конверсия</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(totals?.avgConversionRate ?? 0).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">По команде</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Конверсия по сотрудникам</CardTitle>
          </CardHeader>
          <CardContent>
            {conversionChartData.length === 0 ? (
              <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                Нет данных
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={conversionChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value, name) => {
                      const nameStr = String(name);
                      if (nameStr === 'rate') {return [`${value}%`, 'Конверсия'];}
                      return [value, nameStr === 'orders' ? 'Заказов' : 'Конверсий'];
                    }}
                  />
                  <Bar dataKey="orders" fill="#3b82f6" name="orders" />
                  <Bar dataKey="conversions" fill="#22c55e" name="conversions" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Processing Time Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Время обработки (мин)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isProcessingLoading ? (
              <Skeleton className="h-[250px]" />
            ) : (processingChartData.length === 0 ? (
              <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                Нет данных о времени обработки
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={processingChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value, name) => {
                      const labels: Record<string, string> = {
                        avg: 'Среднее',
                        median: 'Медиана',
                        min: 'Минимум',
                        max: 'Максимум',
                      };
                      const numValue = typeof value === 'number' ? value : 0;
                      const nameStr = String(name);
                      return [`${numValue.toFixed(1)} мин`, labels[nameStr] ?? nameStr];
                    }}
                  />
                  <Line type="monotone" dataKey="avg" stroke="#3b82f6" strokeWidth={2} />
                  <Line type="monotone" dataKey="median" stroke="#22c55e" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Staff Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Детализация по сотрудникам</CardTitle>
        </CardHeader>
        <CardContent>
          {metrics.length === 0 ? (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground">
              Нет данных о сотрудниках
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Сотрудник</TableHead>
                  <TableHead className="text-right">Заказов</TableHead>
                  <TableHead className="text-right">Смен статуса</TableHead>
                  <TableHead className="text-right">Заметок</TableHead>
                  <TableHead className="text-right">Конверсий</TableHead>
                  <TableHead className="text-right">Конверсия %</TableHead>
                  <TableHead className="text-right">Время (мин)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.map((staff) => (
                  <TableRow key={staff.adminUserId}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{staff.username}</p>
                        <p className="text-xs text-muted-foreground">{staff.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {staff.ordersProcessed.toLocaleString('ru-RU')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {staff.statusChanges.toLocaleString('ru-RU')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {staff.notesAdded.toLocaleString('ru-RU')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <Badge
                        variant={staff.conversions > 0 ? 'default' : 'secondary'}
                        className="font-mono"
                      >
                        {staff.conversions}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span
                        className={
                          staff.conversionRate >= 50
                            ? 'text-green-600 font-medium'
                            : (staff.conversionRate >= 25
                            ? 'text-amber-600'
                            : 'text-muted-foreground')
                        }
                      >
                        {staff.conversionRate.toFixed(1)}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {staff.avgProcessingTimeMinutes !== null
                        ? `${staff.avgProcessingTimeMinutes.toFixed(0)}`
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
